import { Directive, EmbeddedViewRef, Inject, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { ConditionOperator, PlatformFeaturesKey, SettingsModel } from '../models';
import { SETTINGS_SERVICE } from '../constants';
import { BaseSettingsGetService } from '../services';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
    selector: '[ngIfFeature]',
    standalone: false
})
export class NgIfFeatureDirective implements OnInit, OnDestroy {
  @Input() set ngIfFeature(featureKey: PlatformFeaturesKey | PlatformFeaturesKey[]) {
    this.featureKey$.next(featureKey);
  }

  @Input() public ngIfFeatureElse?: TemplateRef<unknown>;
  @Input() public ngIfFeatureMode = ConditionOperator.OR;

  private viewRef: EmbeddedViewRef<Object> = null;

  private unsubscribe = new Subject<void>();

  private featureKey$ = new BehaviorSubject<PlatformFeaturesKey | PlatformFeaturesKey[] | null>(null);

  constructor(
    private readonly viewContainer: ViewContainerRef,
    @Inject(SETTINGS_SERVICE) private readonly settingsService: BaseSettingsGetService,
    private readonly templateRef: TemplateRef<any>) {
  }

  ngOnInit(): void {
    combineLatest([
      this.settingsService.getSettings(),
      this.featureKey$
    ])
    .pipe(
      takeUntil(this.unsubscribe)
    ).subscribe(([settings, keys]) => this.updateViewForFeature(settings, keys))
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private updateViewForFeature(settings: SettingsModel, featureKey: PlatformFeaturesKey | PlatformFeaturesKey[]) {
    if (!settings?.features || !featureKey || featureKey.length === 0) {
      this.initializeView(true);
      return;
    }

    const enabled = this.isFeatureEnabled(settings, featureKey);
    this.initializeView(enabled);
  }

  private isFeatureEnabled(settings: SettingsModel, featureKey: PlatformFeaturesKey | PlatformFeaturesKey[]): boolean {
    const requiredKeys: string[] = Array.isArray(featureKey) ? featureKey : [featureKey];

    if (this.ngIfFeatureMode === ConditionOperator.AND) {
      return requiredKeys.every(key => settings.features?.[key]);
    }

    return Object.entries(settings.features).some(([key, enabled]) => requiredKeys.includes(key) && enabled);
  }

  private initializeView(shouldDisplayElement: boolean): void {
    if (!shouldDisplayElement && !this.ngIfFeatureElse) {
      this.viewRef = null;
      this.viewContainer.clear();
      return;
    }

    if (!this.viewRef) {
      this.viewContainer.clear();
      const templateRef = shouldDisplayElement ? this.templateRef : this.ngIfFeatureElse;
      this.viewRef = this.viewContainer.createEmbeddedView(templateRef);
      this.viewRef.detectChanges();
    }
  }

}
